"use client";

import React, { useEffect, useState } from "react";
import { columns } from "./columns";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { RootState } from "@/store/store";
import { fetchInventoryRequest } from "@/store/slices/Inventory.slice";
import { Variant } from "@/types/models/product";
import { DataView } from "@/components/common/DataView";
import { ActionButton } from "@/components/common/DataViewTypes";
import { NavigationTabs } from "@/components/common/navigationTabs";
import { TableContainer } from "@/components/common/TableContainer";
import { RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";

const inventoryTabs = [
  { label: "In Stock", filter: "in_stock" },
  { label: "Low Stock", filter: "low_stock" },
  { label: "Out of Stock", filter: "out_of_stock" },
  { label: "All", filter: null },
];

export default function InventoryPage() {
  const dispatch = useAppDispatch();
  const { items, loading, pagination, error } = useAppSelector((state: RootState) => state.Inventory);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>("");
  // Default to showing all inventory to avoid hiding API results by default
  const [activeTab, setActiveTab] = useState<string | null>(null);

  // Filter items based on stock status
  const getFilteredItems = () => {
    if (!items) return [];

    switch (activeTab) {
      case "in_stock":
        return items.filter(item => (item.inventory || 0) > 10);
      case "low_stock":
        return items.filter(item => {
          const inv = item.inventory || 0;
          return inv > 0 && inv <= 10;
        });
      case "out_of_stock":
        return items.filter(item => (item.inventory || 0) === 0);
      default:
        return items;
    }
  };

  const filteredItems = getFilteredItems();

  // Further filter by search query
  const searchFilteredItems = searchQuery.trim()
    ? filteredItems.filter(item =>
        item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.product_slug?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : filteredItems;

  useEffect(() => {
    // Only fetch if we don't have data OR if page changed
    if ((items?.length || 0) === 0 || pageIndex > 0) {
      dispatch(fetchInventoryRequest({ page: pageIndex + 1, pageSize: pagination.pageSize }));
    }
  }, [dispatch, pageIndex, pagination.pageSize, items?.length]);

  useEffect(() => {
    if (error) toast.error(error.message);
  }, [error]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPageIndex(0);
  };

  const handleTabChange = (filter: string | null) => {
    setActiveTab(filter);
    setPageIndex(0);
  };

  const handlePageChange = (newPageIndex: number) => setPageIndex(newPageIndex);

  // Handle refresh - directly dispatch action to force a fresh fetch
  const handleRefresh = () => {
    dispatch(fetchInventoryRequest({ page: pageIndex + 1, pageSize: pagination.pageSize }));
    toast.success("Inventory refreshed");
  };

  const getStatusTitle = () => {
    if (!activeTab) return "All Inventory";
    switch (activeTab) {
      case "in_stock":
        return "In Stock Items";
      case "low_stock":
        return "Low Stock Items";
      case "out_of_stock":
        return "Out of Stock Items";
      default:
        return "Inventory";
    }
  };

  const getStatusDescription = () => {
    if (!activeTab) return "Monitor and manage stock levels for all product variants";
    switch (activeTab) {
      case "in_stock":
        return "Variants with sufficient stock levels (more than 10 units)";
      case "low_stock":
        return "Variants running low on stock (10 units or less)";
      case "out_of_stock":
        return "Variants that need restocking";
      default:
        return "Monitor and manage stock levels";
    }
  };

  // Action buttons with refresh
  const actionButtons: ActionButton[] = [
    {
      label: "Refresh",
      icon: <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />,
      onClick: handleRefresh,
      variant: "outline",
      disabled: loading,
    },
    {
      label: "Export",
      icon: <Download className="h-3 w-3" />,
      onClick: () => toast.info("Export functionality coming soon"),
      variant: "outline",
    },
  ];

  // Calculate pagination for filtered data
  const currentPagination = {
    totalPages: Math.ceil(searchFilteredItems.length / (pagination.pageSize || 10)) || 1,
    currentPage: pageIndex + 1,
    totalItems: searchFilteredItems.length,
    pageSize: pagination.pageSize || 10,
    total: searchFilteredItems.length,
  };

  // Paginate the filtered items
  const paginatedItems = searchFilteredItems.slice(
    pageIndex * (pagination.pageSize || 10),
    (pageIndex + 1) * (pagination.pageSize || 10)
  );

  return (
    <div className="space-y-4">
      <TableContainer
        navigationTabs={
          <NavigationTabs
            tabs={inventoryTabs.map((tab) => ({
              label: tab.label,
              onClick: () => handleTabChange(tab.filter),
              isActive: activeTab === tab.filter,
            }))}
            hasBackButton={false}
          />
        }
      >
        <div className="bg-secondary-background p-[18px] border rounded-[16px]">
          <DataView
            columns={columns}
            data={paginatedItems}
            pagination={currentPagination}
            pageIndex={pageIndex}
            onPageChange={handlePageChange}
            loading={loading}
            error={error?.message || null}
            title={getStatusTitle()}
            description={getStatusDescription()}
            searchPlaceholder="Search by product, SKU, or slug..."
            searchQuery={searchQuery}
            onSearch={handleSearch}
            actionButtons={actionButtons}
            enableColumnResizing={true}
            enableRowSelection={true}
            emptyStateType="inventory"
            loadingMessage="Loading inventory..."
          />
        </div>
      </TableContainer>
    </div>
  );
}
